<template>
	<b-modal ref="modal" size="xl" hide-footer>
		<template v-slot:modal-title>
			{{ $t('invoice.supplier_article_prices_title', { label }) }}
		</template>

		<LoadingSpinner v-if="loading" />

		<div v-else-if="!points.length" class="alert alert-info mb-0">
			{{ $t('invoice.supplier_article_prices_empty') }}
		</div>

		<template v-else>
			<div v-if="mixed_units" class="alert alert-warning">
				{{ $t('invoice.supplier_article_prices_mixed_units') }}
			</div>

			<apexchart type="line" height="350" :options="chart_options" :series="series" />

			<h3 class="mt-4">{{ $t('invoice.supplier_article_prices_key_dates') }}</h3>

			<table class="table table-sm mb-0">
				<thead>
					<tr>
						<th>{{ $t('invoice.supplier_article_prices_date') }}</th>
						<th class="text-right">{{ $t('invoice.supplier_article_prices_previous') }}</th>
						<th class="text-right">{{ $t('invoice.supplier_article_prices_price') }}</th>
						<th class="text-right">{{ $t('invoice.supplier_article_prices_variation') }}</th>
						<th>{{ $t('invoice.supplier_article_prices_invoices') }}</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="change in key_dates" :key="change.date">
						<td>{{ formatDate(change.date) }}</td>
						<td class="text-right">{{ change.previous === null ? '' : priceLabel(change.previous) }}</td>
						<td class="text-right">{{ priceLabel(change.price) }}</td>
						<td class="text-right">{{ change.variation === null ? '' : change.variation }}</td>
						<td>
							<router-link
								v-for="invoice in change.invoices"
								:key="invoice.id"
								class="mr-2"
								:to="{ name: 'invoiceSupplierFicheOverview', params: { invoicesupplier_id: invoice.id } }"
								@click.native="$refs.modal.hide()"
							>
								{{ invoice.num || invoice.id }}
							</router-link>
						</td>
					</tr>
				</tbody>
			</table>
		</template>
	</b-modal>
</template>

<script type="text/javascript">
	import VueApexCharts from 'vue-apexcharts'
	import Invoice from "@/mixins/Invoice.js"

	export default {
		name: "ArticlePriceModal",
		mixins: [Invoice],
		data() {
			return {
				label: '',
				points: [],
				loading: false
			}
		},
		computed: {
			/**
			 * Tarif moyen de chaque jour de facture, du plus ancien au plus récent. Plusieurs lignes d'un
			 * même jour — une par cheval sur une facture de pension — ne font qu'un point de la courbe.
			 */
			days() {
				const days = {}

				this.points.forEach(point => {
					if(!days[point.date]) days[point.date] = { date: point.date, total: 0, count: 0, invoices: {} }

					days[point.date].total += point.price
					days[point.date].count++
					days[point.date].invoices[point.invoice_id] = { id: point.invoice_id, num: point.invoice_num }
				})

				return Object.values(days)
					.sort((a, b) => a.date.localeCompare(b.date))
					.map(day => ({ date: day.date, price: day.total / day.count, invoices: Object.values(day.invoices) }))
			},

			/**
			 * Dates où le tarif moyen change par rapport au jour facturé précédent, la première comprise.
			 */
			key_dates() {
				const changes = []
				let previous = null

				this.days.forEach(day => {
					if(previous === null || Math.abs(day.price - previous) > 0.00001) {
						changes.push({
							date: day.date,
							previous,
							price: day.price,
							variation: previous ? ((day.price - previous) / previous * 100).toFixed(1) + ' %' : null,
							invoices: day.invoices
						})
					}

					previous = day.price
				})

				return changes.reverse()
			},

			/**
			 * Un tarif ne se compare qu'à unité et quantité de base égales : sans conversion, un changement
			 * de conditionnement ferait mentir la courbe, et l'écran le signale plutôt que de le taire.
			 */
			mixed_units() {
				return new Set(this.points.map(point => point.unit + '|' + point.base_qty)).size > 1
			},

			series() {
				return [
					{
						name: this.$t('invoice.supplier_article_prices_daily'),
						type: 'line',
						data: this.days.map(day => ({ x: Date.parseTz(day.date).getTime(), y: day.price }))
					},
					{
						name: this.$t('invoice.supplier_article_prices_lines'),
						type: 'scatter',
						data: this.points.map(point => ({ x: Date.parseTz(point.date).getTime(), y: point.price, point }))
					}
				]
			},

			chart_options() {
				return {
					chart: { toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } },
					stroke: { width: [2, 0] },
					markers: { size: [4, 6] },
					xaxis: { type: 'datetime' },
					yaxis: { labels: { formatter: value => this.priceLabel(value) } },
					tooltip: { shared: false, intersect: true, custom: this.tooltip }
				}
			}
		},
		methods: {
			/**
			 * Ouvre la modale sur l'historique d'un article chez un fournisseur.
			 *
			 * @param {String} label Libellé de l'article, tel que l'utilisateur l'a retenu
			 * @param {Number} tiers_id Fournisseur
			 */
			async open(label, tiers_id) {
				this.label = label
				this.points = []
				this.loading = true
				this.$refs.modal.show()

				this.points = await this.getSupplierArticlePrices(tiers_id, label)
				this.loading = false
			},

			priceLabel(value) {
				return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
			},

			/**
			 * Infobulle d'un point : la ligne de facture qu'il représente, ou la moyenne du jour pour la
			 * courbe. Le numéro de facture est échappé, il vient du document du fournisseur.
			 */
			tooltip({ seriesIndex, dataPointIndex, w }) {
				const data = w.config.series[seriesIndex].data[dataPointIndex]
				const lines = [this.formatDate(new Date(data.x)), this.priceLabel(data.y)]

				if(data.point) {
					const point = data.point
					lines.push(this.$t('invoice.supplier_article_prices_invoice') + ' ' + (point.invoice_num || point.invoice_id))
					lines.push(point.qte + ' ' + this.unitLabel(point.unit) + (point.base_qty != 1 ? ' — ' + this.$t('invoice.supplier_article_prices_base', { base: point.base_qty }) : ''))
				}

				const escape = text => String(text).replace(/[&<>"']/g, char => '&#' + char.charCodeAt(0) + ';')

				return '<div class="p-2">' + lines.map(escape).join('<br>') + '</div>'
			}
		},
		components: {
			apexchart: VueApexCharts,
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
		}
	}
</script>
